/* eslint-disable max-statements */

import { modals } from '@mantine/modals';
import numeral from 'numeral';

import { Declaration, DeclarationGeneralInfo, DeclarationGoods } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { Table, TableActionEnum, TableActions, TableColumnProps } from '@components';
import { useDeleteScreenshot, useDownloadScreenshot } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { declarationModalManager } from '../../declarationModalManager';
import { GoodsItemStatusIndicator } from './GoodsItemStatusIndicator';

interface DeclarationViewGoodListProps {
  editDisabled: boolean;
  generalInfo?: DeclarationGeneralInfo;
  declaration: Declaration;
}

export const DeclarationViewGoodList: React.FC<DeclarationViewGoodListProps> = ({ editDisabled, generalInfo, declaration }) => {
  const deleteScreenshotMutation = useDeleteScreenshot();
  const downloadScreenshotMutation = useDownloadScreenshot();

  const { data: settings } = useSettings();

  const isHighValue = isHighValueDeclaration(declaration?.generalInfo?.group);

  const openShowRiskAnalysisModal = (goodDetails: DeclarationGoods) => declarationModalManager.openRiskAnalysis(declaration._id, goodDetails);

  const openShowUploadScreenshotModal = (goodDetails: DeclarationGoods) => declarationModalManager.openUploadScreenshot(declaration, goodDetails);

  const openGoodsItem = (goodDetails: DeclarationGoods) => {
    declarationModalManager.openGoodsItem(declaration, goodDetails, editDisabled);
  };

  const openDeleteScreenshot = (goodDetails: DeclarationGoods) => {
    modals.openConfirmModal({
      title: 'Confirm screenshot delete',
      children: <div>Are you sure you want to delete the screenshot? Action can't be undone!</div>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteScreenshotMutation.mutate({ goodItemId: goodDetails._id, id: declaration._id });
      },
    });
  };

  const handleTableAction = (type: string, row: DeclarationGoods) => {
    switch (type) {
      case 'view': {
        openGoodsItem(row);
        return;
      }
      case TableActionEnum.Refactor: {
        openShowRiskAnalysisModal(row);
        return;
      }
      case TableActionEnum.DownloadImage: {
        const screenshotLink =
          row.webScraping.webAdditionalFiles.find((webAdditionalFile: any) => {
            return webAdditionalFile.type === 'screenshot';
          })?.url || '';

        downloadScreenshotMutation.mutate({ screenshotLink });
        return;
      }
      case TableActionEnum.DownloadImageDisabled: {
        if (generalInfo?.webScrapingDone) {
          openShowUploadScreenshotModal(row);
        }
        return;
      }
      case TableActionEnum.Delete:
        openDeleteScreenshot(row);
        break;
      case 'viewDocuments':
        declarationModalManager.openDocumentsModal(declaration, row, editDisabled);
        break;

      default:
        break;
    }
  };

  const lowValueColumns = [
    {
      name: 'Package',
      cell: (row) => row.packaging.numberOfPackages,
    },
    {
      name: 'Intrinsic value',
      width: '150px',
      cell: (row: DeclarationGoods) =>
        row.goodsMeasure.intrinsicValue ? `${numeral(row.goodsMeasure.intrinsicValue).format('0.00')} ${row.goodsMeasure.currencyCode}` : 'N/A',
    },
  ];

  const highValueColumns = [
    {
      name: 'Quantity',
      cell: (row: DeclarationGoods) => {
        let totalQuantity = 0;

        if (row?.dutiesAndTaxes?.items?.length) {
          totalQuantity = row?.dutiesAndTaxes?.items[0].taxBase[0]?.quantity || 0;
        }

        return totalQuantity;
      },
    },
    {
      name: 'Item amount invoiced',
      cell: (row) => row.itemAmountInvoiced,
    },
  ];

  const columns: TableColumnProps<DeclarationGoods>[] = [
    {
      name: 'Checked',
      cell: (row: any) => {
        return <GoodsItemStatusIndicator goodDetails={row} generalInfo={generalInfo} />;
      },
    },
    {
      name: 'Goods item',
      cell: (row) => row.goodsItemNumber,
    },
    {
      name: 'Description',
      cell: (row) => row.descriptionOfGoods,
    },
    {
      name: 'HS Code',
      cell: (row) => row.commodityCode.hsCode,
    },
    ...(isHighValue ? highValueColumns : lowValueColumns),
    {
      name: 'Gross Mass',
      cell: (row) => row.goodsMeasure.grossMass,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: any, rowIdx) => {
        const actions = [];
        if (isHighValue) {
          actions.push({
            icon: IconFactory.refactor,
            text: 'Update Risk Analysis',
            onClick: () => handleTableAction(TableActionEnum.Refactor, row),
          });
          if (row.webScraping?.webAdditionalFiles?.find((item: any) => item.type === 'screenshot')) {
            actions.push({
              icon: IconFactory.viewImage,
              text: 'Download image',
              onClick: () => handleTableAction(TableActionEnum.DownloadImage, row),
            });
            actions.push({
              icon: IconFactory.delete,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row),
            });
          } else {
            actions.push({
              icon: IconFactory.viewImage,
              text: 'Download image',
              onClick: () => handleTableAction(TableActionEnum.DownloadImageDisabled, row),
            });
          }
        }

        if (!isHighValue) {
          actions.push({
            icon: IconFactory.report,
            text: 'View documents',
            onClick: () => handleTableAction('viewDocuments', row),
          });
        }

        if (!settings?.isForwarder) {
          actions.push({
            icon: IconFactory.view,
            text: 'View details',
            onClick: () => handleTableAction('view', row),
          });
        }

        return <TableActions actions={actions} data-pw={rowIdx} />;
      },
    },
  ];

  return <Table columns={columns} dataSource={declaration?.goodsShipmentItems || []} isLoading={false} pagination verticalSpacing="xs" />;
};
